import { api } from '@/api';
import { createEntityAttachmentApi } from '@/api/common';
import type {
  ServiceContract,
  ServiceContractDocument,
  CreateServiceContractRequest,
  PatchServiceContractRequest,
} from '../types';

// contractAttachments exposes the shared entity-attachment endpoints for one contract.
// Reused by the upload hook (create + enable) and the delete hook (remove).
export function contractAttachments(contractId: string) {
  return createEntityAttachmentApi(`/v1/service-contracts/${contractId}`);
}

// fetchDeviceContracts returns all non-deleted contracts for a device (admin).
export async function fetchDeviceContracts(deviceId: string): Promise<ServiceContract[]> {
  return api.get<ServiceContract[]>(`/v1/devices/${deviceId}/service-contracts`);
}

// createServiceContract creates an active contract for a device without a current one.
export async function createServiceContract(
  request: CreateServiceContractRequest
): Promise<ServiceContract> {
  return api.post<ServiceContract>('/v1/service-contracts', request);
}

// patchServiceContract applies a lifecycle transition (activate/lock/unlock/cancel).
// Invalid transitions are rejected by the API with 409 Conflict.
export async function patchServiceContract(
  contractId: string,
  request: PatchServiceContractRequest
): Promise<ServiceContract> {
  return api.patch<ServiceContract>(`/v1/service-contracts/${contractId}`, request);
}

// deleteServiceContract permanently removes a contract, independent of its lifecycle status.
export async function deleteServiceContract(contractId: string): Promise<void> {
  return api.delete<void>(`/v1/service-contracts/${contractId}`);
}

// fetchContractDocuments returns all non-deleted documents for a contract.
export async function fetchContractDocuments(
  contractId: string
): Promise<ServiceContractDocument[]> {
  return api.get<ServiceContractDocument[]>(
    `/v1/service-contracts/${contractId}/documents`
  );
}

// getContractDocumentUrl resolves a signed download URL for a specific document (admin).
export async function getContractDocumentUrl(
  contractId: string,
  documentId: string
): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/v1/service-contracts/${contractId}/documents/${documentId}/document-url`
  );
  return response.url;
}

// getLatestContractDocumentUrl resolves a signed download URL for the latest available
// document on the device's current contract (used by the read-only view).
export async function getLatestContractDocumentUrl(deviceId: string): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/v1/devices/${deviceId}/service-contract/document-url`
  );
  return response.url;
}
